import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import {
  usePostPatientSurveySubmission,
  useSurveysFromName
} from "../../queries/useSurveys"
import { IAnswer, IPatientSurvey, IQuestion } from "../../types/types"
import {
  checkQuestionsConsistency,
  formatAPIPhone,
  isAnswerChecked,
  isQuestionKey,
  validateConsultationDate,
  validateHeight,
  validatePhoneNumber,
  validateWeight
} from "../../utils/validations"
import { sortByQuestionList } from "../../utils/surveys"

type SurveyProps = {
  patientSurvey: IPatientSurvey
  patientId: number
  changeSurvey: () => void
  isUp?: boolean
}

const question_ref_scales = [
  "filter_lumbar_pain_scale",
  "filter_leg_pain_scale",
  "filter_neck_pain_scale",
  "filter_arm_pain_scale"
]

const orderedQuestionsBySurveys = {
  filter_phase_2_medical_information: [
    "back_surgery_history",
    "principal_consultation_reason",
    "principal_symptom_side",
    "symptoms_beginning",
    "work_accident",
    "filter_muscular_weakness",
    "filter_muscular_weakness_comment",
    "urinal_problems",
    "lumbar_imaging",
    "lumbar_infiltration",
    "physiotherapy"
  ]
}

const questionLabelStyle = "text-cortexx-900 text-sm text-start"
const highlightedQuestionLabelStyle = `${questionLabelStyle} font-bold`

const Survey = (props: SurveyProps) => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = usePostPatientSurveySubmission(
    props.patientId,
    props.patientSurvey.id
  )

  const { data: survey } = useSurveysFromName(
    props.patientSurvey.survey_reference
  )

  const [answers, setAnswers] = useState<Array<IAnswer>>()
  const [highlightUnansweredQuestions, setHighlightUnansweredQuestions] =
    useState<boolean>(false)
  const [weightError, setWeightError] = useState<string | undefined>(undefined)
  const [heightError, setHeightError] = useState<string | undefined>(undefined)
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>(
    undefined
  )
  const [consultationDateError, setConsultationDateError] = useState<
    string | undefined
  >(undefined)

  const { questions: unOrderedQuestions } = checkQuestionsConsistency(
    survey?.questions,
    answers
  )

  const questions =
    props.patientSurvey.survey_reference ===
      "filter_phase_2_medical_information" && unOrderedQuestions
      ? sortByQuestionList(
          orderedQuestionsBySurveys.filter_phase_2_medical_information,
          unOrderedQuestions
        )
      : unOrderedQuestions

  const setConsistentAnswers = (answersToSet: Array<IAnswer>) => {
    const { answers: _answers } = checkQuestionsConsistency(
      survey?.questions,
      answersToSet
    )
    setAnswers(_answers)
  }

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value, id } = event.target
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    const _question = survey?.questions.find(
      (question) => question.id === questionId
    )
    const selectedOption = _question?.options.find(
      (option) => option.label === value
    )

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: selectedOption?.value || 0 }
    ])
  }

  const handleWeightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeightError(validateWeight(event.target.value))
    handleInputChange(event)
  }

  const handleHeightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeightError(validateHeight(event.target.value))
    handleInputChange(event)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, id } = event.target
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: parseInt(value) || 0 }
    ])
  }

  const handlePhoneNumberChange = (
    questionId: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target

    setPhoneNumberError(validatePhoneNumber(value))
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers(
      value
        ? [
            ..._answers,
            { question_id: questionId, value: formatAPIPhone(value) || "" }
          ]
        : _answers
    )
  }

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: value || "" }
    ])
  }

  const handleSingleChoiceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const _val = parseInt(value)
    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: _val }
    ])
  }

  const handleConsultationDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value, id } = event.target
    const error = validateConsultationDate(value)
    setConsultationDateError(error)

    const questionId = parseInt(id)
    const _answers =
      answers?.filter((answer) => answer.question_id !== questionId) || []

    setConsistentAnswers([
      ..._answers,
      { question_id: questionId, value: value || "" }
    ])
  }

  const checkAllQuestionsAnswered = () =>
    survey?.questions.every((question) => {
      if (question.kind !== "TEXT") {
        return answers
          ? !!answers?.find((answer) => answer.question_id === question.id)
          : false
      }
      return true
    })

  const onSubmitPatientSurvey = () => {
    if (
      !checkAllQuestionsAnswered() ||
      weightError ||
      heightError ||
      phoneNumberError ||
      consultationDateError
    ) {
      setHighlightUnansweredQuestions(true)
    } else {
      mutateAsync({
        submission: {
          answers_attributes: answers || []
        }
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ["surveys"] })
        props.changeSurvey()
      })
    }
  }

  const getAnswerFromQuestionId = (questionId: number) => {
    return answers?.find((_ans) => _ans.question_id === questionId)
  }

  const renderQuestionOptions = (question: IQuestion) => {
    if (question.kind === "UNIQUE" && question.options.length > 3) {
      return question_ref_scales.includes(question.question_reference) ? (
        <div className="flex flex-row">
          <div className="px-4">0</div>
          <div>
            <input
              type="range"
              id={question.id.toString()}
              onChange={handleInputChange}
              name="pain_scale"
              min="0"
              max="10"
              step="1"
              defaultValue="0"
              list="markers"
              className="w-[275px] slider accent-cortexx-550"
            />
            <datalist id="markers" className="flex justify-between">
              {question.options.map((option) => (
                <option
                  className={
                    getAnswerFromQuestionId(question.id)?.value.toString() ===
                    option.value.toString()
                      ? "text-cortexx-500 font-bold w-[20px] text-center"
                      : "text-cortexx-550 w-[20px] text-center"
                  }
                  value={option.value}
                  label={option.label}
                ></option>
              ))}
            </datalist>
          </div>
          <div className="px-4">10</div>

          <div className="flex justify-center items-center">
            <div className="font-semibold text-cortexx-500 border-cortexx-550 border rounded-xl justify-center items-center flex ml-4 w-12 h-6">
              {getAnswerFromQuestionId(question.id)?.value.toString()}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative w-full">
          <select
            name={question.question_reference}
            id={question.id.toString()}
            className="mt-2 border-2 border-cortexx-500 rounded-md p-2 w-full md:w-2/3 focus:border-cortexx-550 block md:text-sm text-cortexx-900"
            onChange={handleSelectChange}
          >
            <option className="w-screen" value="" selected disabled hidden>
              Sélectionnez une valeur
            </option>
            {question.options.map((option) => (
              <option
                key={option.label}
                className="w-screen"
                value={option.label}
              >
                {option.label
                  .replace("(> 10 MET):", "- ")
                  .replace("(7-10 MET):", "- ")
                  .replace("(4-7 MET):", "- ")
                  .replace("(< 4 MET):", "- ")}
              </option>
            ))}
          </select>
        </div>
      )
    } else if (question.kind === "UNIQUE" && question.options.length <= 3) {
      return (
        <div className="flex flex-row mt-2 md:mt-3">
          {question.options.map((option) => (
            <div className="flex flex-row mr-8">
              <input
                type="checkbox"
                name={question.question_reference}
                id={question.id.toString()}
                value={option.value}
                onChange={handleSingleChoiceChange}
                checked={isAnswerChecked(
                  question.id,
                  answers ?? [],
                  option.value
                )}
              />
              <label className="ml-2">{option.label}</label>
            </div>
          ))}
        </div>
      )
    } else if (question.kind === "NUMBER") {
      return (
        <>
          <div className="flex-col">
            <input
              type="number"
              id={question.id.toString()}
              className="mt-2 border-2 border-cortexx-500 rounded-md py-2 w-42 justify-end text-right px-2 md:px-0"
              onChange={
                question.question_reference === "weight"
                  ? handleWeightChange
                  : question.question_reference === "height"
                  ? handleHeightChange
                  : handleInputChange
              }
              placeholder={
                question.question_reference === "weight"
                  ? "70"
                  : question.question_reference === "height"
                  ? "170"
                  : "0"
              }
            />
            <label className="text-cortexx-550 text-sm">
              {question.question_reference === "weight"
                ? " kg"
                : question.question_reference === "height"
                ? " cm"
                : null}
            </label>
          </div>
          {weightError && question.question_reference === "weight" && (
            <div className="text-cortexx-800 text-xs w-72 text-left pt-2">
              {weightError}
            </div>
          )}
          {heightError && question.question_reference === "height" && (
            <div className="text-cortexx-800 text-xs w-72 text-left pt-2">
              {heightError}
            </div>
          )}
        </>
      )
    } else if (question.kind === "DATE") {
      return (
        <div className="py-2">
          <input
            className="border-2 border-slate-300 rounded-xl w-72 py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none"
            id={question.id.toString()}
            type="date"
            onChange={
              question.question_reference === "filter_consultation_date"
                ? handleConsultationDateChange
                : handleInputChange
            }
          />
          {consultationDateError && (
            <div className="text-cortexx-800 text-xs w-72 text-justify">
              {consultationDateError}
            </div>
          )}
        </div>
      )
    } else if (question.kind === "TEXT") {
      return question.question_reference === "phone_number" ? (
        <>
          <input
            type="tel"
            id={"phone"}
            className="mt-2 border-2 border-cortexx-500 rounded-md p-2 w-42 justify-end text-right"
            onChange={(e) => handlePhoneNumberChange(question.id, e)}
            pattern="[0-9 ]+"
            placeholder={"0110111001"}
          />
          {phoneNumberError && (
            <div className="text-cortexx-800 text-xs w-72 text-left pt-2">
              {phoneNumberError}
            </div>
          )}
        </>
      ) : (
        <input
          type="text"
          id={question.id.toString()}
          className="mt-2 border-2 border-cortexx-500 rounded-md p-2 w-full"
          onChange={handleTextInputChange}
          placeholder={
            question.question_reference === "recommandation_practitioner_name"
              ? "Jean Dupond"
              : question.content
          }
        />
      )
    }
    return null
  }

  const renderQuestion = (question: IQuestion, index: number) => {
    const missingHighLightQuestion =
      highlightUnansweredQuestions &&
      (question.kind !== "TEXT" ||
        question.question_reference === "phone_number") &&
      answers &&
      !answers.find((_ans) => _ans.question_id === question.id)
    const highLightQuestion = isQuestionKey(question)

    return (
      <form key={question.id} className="flex flex-col items-start my-2">
        <label
          className={
            missingHighLightQuestion || highLightQuestion
              ? highlightedQuestionLabelStyle
              : questionLabelStyle
          }
        >
          {missingHighLightQuestion ? (
            <span className="text-cortexx-800 text-base align-super">*</span>
          ) : null}
          {index + 1}. {question.content}
        </label>
        {renderQuestionOptions(question)}
      </form>
    )
  }

  return (
    <div
      className={`flex flex-col md:rounded-xl md:border h-full w-screen md:w-[700px] bg-cortexx-50 transition-opacity ${
        !props.isUp
          ? "opacity-0 ease-out duration-700"
          : "opacity-100 ease-in duration-700"
      }`}
    >
      <div className="flex flex-col border-b text-cortexx-900 px-3 pt-3 md:h-1/6 md:px-6 md:pt-6">
        <h2 className="font-semibold md:text-xl">
          {survey?.content} ({survey?.questions.length} questions)
        </h2>
        <div className="text-sm my-4 md:w-3/4">{survey?.description}</div>
      </div>
      <div className="flex flex-col h-5/6 text-cortexx-900 text-sm overflow-y-auto px-4 pt-4 border-b">
        {questions?.map((question, index) => renderQuestion(question, index))}
      </div>
      <div className="flex items-end justify-end">
        <div className="flex flex-col items-end justify-end pr-6 pb-6 w-full">
          {!isPending ? (
            <button
              className="mt-4 bg-cortexx-500 text-white font-semibold py-2 px-4 rounded-xl hover:bg-cortexx-550 transition-colors"
              onClick={onSubmitPatientSurvey}
              disabled={false}
              type="button"
            >
              <div className="my-1 mx-2">
                <p>Suivant</p>
              </div>
            </button>
          ) : (
            <button className="mt-4 bg-cortexx-100 text-cortexx-500 font-semibold py-2 px-4 rounded-xl">
              <div className="flex-row flex items-end justify-end mx-2 my-1">
                <p>Envoi</p>
                <div className="ml-2">
                  <img
                    src="/images/fade-stagger-circles.svg"
                    style={{ height: "20px" }}
                    alt="close"
                    loading="lazy"
                  />
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Survey
